import React, { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!token) {
      toast.error("Reset link is missing a token.");
      return;
    }
    if (password !== confirm) {
      toast.error("Passwords do not match.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Password updated. Please sign in.");
      nav("/login");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Reset failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-6" data-testid="reset-page">
      <div className="w-full max-w-md fade-up">
        <Link to="/login" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
          <ArrowLeft className="w-4 h-4" aria-hidden="true" /> Back to sign in
        </Link>
        <p className="mt-8 text-xs tracking-[0.3em] uppercase text-muted-foreground">Almost done</p>
        <h1 className="mt-3 font-serif text-4xl tracking-tight">Set a new password</h1>
        <p className="mt-3 text-muted-foreground">Make it something only you would think of.</p>

        {!token && (
          <p className="mt-6 text-sm text-destructive" data-testid="reset-no-token">
            No reset token found in the URL. Request a new link.
          </p>
        )}

        <form onSubmit={submit} className="mt-10 space-y-6">
          <div>
            <Label htmlFor="password" className="text-xs uppercase tracking-widest text-muted-foreground">New password</Label>
            <Input
              id="password"
              data-testid="reset-password-input"
              type="password"
              required
              minLength={6}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-2 h-12 rounded-none border-0 border-b-2 bg-transparent px-0 focus-visible:ring-0 focus-visible:border-primary text-base"
              placeholder="At least 6 characters"
            />
          </div>
          <div>
            <Label htmlFor="confirm" className="text-xs uppercase tracking-widest text-muted-foreground">Confirm</Label>
            <Input
              id="confirm"
              data-testid="reset-confirm-input"
              type="password"
              required
              minLength={6}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="mt-2 h-12 rounded-none border-0 border-b-2 bg-transparent px-0 focus-visible:ring-0 focus-visible:border-primary text-base"
              placeholder="Repeat new password"
            />
          </div>
          <Button
            type="submit"
            disabled={busy || !token}
            data-testid="reset-submit-btn"
            className="w-full h-12 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
          >
            {busy ? "Updating…" : "Update password"}
          </Button>
        </form>
      </div>
    </div>
  );
}
